import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../api/api";
import { esFechaDeCalendario, formatearFecha, fechaLocal } from "../utils/fechas";

const SERVICE_LABELS = {
  aire_acondicionado: "Aire Acondicionado", albanileria: "Albañilería",
  arquitectura: "Arquitectura", belleza_personal: "Belleza Personal",
  carpinteria: "Carpintería", cerrajeria: "Cerrajería",
  control_plagas: "Control de Plagas", cuidado_mayores: "Cuidado de Mayores",
  cuidado_ninos: "Cuidado de Niños", decoradores: "Decoradores",
  electricista: "Electricista", entrenamiento: "Entrenamiento",
  gasista: "Gasista", herrero: "Herrero", infantiles: "Infantiles (Fiestas)",
  instalador_general: "Instalador General", jardinero: "Jardinero",
  mascotas: "Mascotas", mecanica_automotriz: "Mecánica Automotriz",
  mudanzas: "Mudanzas", piletas: "Piletas", pintores: "Pintores",
  plomeros: "Plomeros", seguridad: "Seguridad",
  servicio_domestico: "Servicio Doméstico", tapicero: "Tapicero",
  tecnologia: "Tecnología",
};

const SERVICE_ICONS = {
  aire_acondicionado: "❄️", albanileria: "🧱", arquitectura: "🏛️",
  belleza_personal: "💇", carpinteria: "🪵", cerrajeria: "🔑",
  control_plagas: "🐛", cuidado_mayores: "👴", cuidado_ninos: "👶",
  decoradores: "🎨", electricista: "⚡", entrenamiento: "💪",
  gasista: "🔥", herrero: "⚒️", infantiles: "🎉",
  instalador_general: "🔩", jardinero: "🌿", mascotas: "🐾",
  mecanica_automotriz: "🚗", mudanzas: "📦", piletas: "🏊",
  pintores: "🖌️", plomeros: "🔧", seguridad: "🔒",
  servicio_domestico: "🧺", tapicero: "🛋️", tecnologia: "💻",
};

const STATUS_CONFIG = {
  created:            { label: "Creada",                color: "#5c7a8a", bg: "rgba(92,122,138,0.1)",  step: 0 },
  waiting_assignment: { label: "Buscando prestador",    color: "#b06a00", bg: "rgba(176,106,0,0.1)",   step: 1 },
  waiting_quotes:     { label: "Esperando presupuestos",color: "#b06a00", bg: "rgba(176,106,0,0.1)",   step: 1 },
  quoted:             { label: "Presupuestos listos",   color: "#163244", bg: "rgba(22,50,68,0.08)",   step: 2 },
  assigned:           { label: "Prestador asignado",    color: "#1b7f4e", bg: "rgba(27,127,78,0.1)",   step: 3 },
  in_progress:        { label: "En progreso",           color: "#1b7f4e", bg: "rgba(27,127,78,0.1)",   step: 3 },
  completed:          { label: "Completada",            color: "#163244", bg: "rgba(22,50,68,0.08)",   step: 4 },
  cancelled:          { label: "Cancelada",             color: "#c0392b", bg: "rgba(192,57,43,0.1)",   step: -1 },
  expired:            { label: "Expirada",              color: "#7b5ea7", bg: "rgba(123,94,167,0.1)",  step: -1 },
};

const font = "Montserrat, sans-serif";

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60)     return "hace un momento";
  if (diff < 3600)   return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400)  return `hace ${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `hace ${Math.floor(diff / 86400)} días`;
  return new Date(dateStr).toLocaleDateString("es-AR");
}

// Formatea valores de campos específicos — las fechas (fecha objetivo, del
// evento…) son de calendario: se muestran sin convertir de zona (utils/fechas)
const formatValue = (v) => (esFechaDeCalendario(v) ? formatearFecha(v) : String(v));
const IconClock = () => <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const IconPin   = () => <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>;
const IconCheck = () => <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>;
const IconChat  = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>;
const IconPlus  = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>;
const IconDollar= () => <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;

// ─── Dispute Modal ────────────────────────────────────────────────────────────
function DisputeModal({ req, onClose, onSuccess }) {
  const REASONS = [
    "El servicio no fue completado",
    "El trabajo fue de mala calidad",
    "El prestador no se presentó",
    "Se cobró un monto diferente al acordado",
    "El prestador causó daños",
    "Otro motivo",
  ];

  const [reason,      setReason]      = useState("");
  const [description, setDescription] = useState("");
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");

  const handleSubmit = async () => {
    if (!reason) { setError("Seleccioná un motivo."); return; }
    setLoading(true);
    setError("");
    try {
      await api.post("/disputes", {
        transaction_id: req.transaction_id,
        reason,
        description: description || undefined,
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || "Error al abrir la disputa.");
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 200,
      background: "rgba(22,50,68,0.55)", backdropFilter: "blur(3px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div style={{ background: "#fff", borderRadius: 18, padding: "32px 28px", width: "min(480px, 100%)", boxShadow: "0 16px 60px rgba(22,50,68,0.22)", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <div style={{ width: 3, height: 13, background: "#E7A432", borderRadius: 2 }} />
            <span style={{ fontFamily: font, fontWeight: 800, fontSize: 16, color: "#163244" }}>Abrir un reclamo</span>
          </div>
          <p style={{ fontFamily: font, fontSize: 13, color: "#7a93a4", margin: 0 }}>
            Servicio: <strong style={{ color: "#163244" }}>{SERVICE_LABELS[req.service_key] || req.service_key}</strong>
          </p>
        </div>

        <div style={{ background: "rgba(231,164,50,0.08)", border: "1px solid rgba(231,164,50,0.3)", borderRadius: 8, padding: "10px 14px", marginBottom: 18, fontSize: 12, color: "#b8820d", fontFamily: font }}>
          ⚠ El equipo de Fixo revisará tu reclamo y te notificará la resolución dentro de las 48hs hábiles.
        </div>

        {/* Motivo */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontFamily: font, fontSize: 12, fontWeight: 700, color: "#163244", display: "block", marginBottom: 8 }}>
            Motivo del reclamo <span style={{ color: "#c0392b" }}>*</span>
          </label>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {REASONS.map(r => (
              <button
                key={r}
                onClick={() => setReason(r)}
                style={{
                  padding: "9px 14px", borderRadius: 8, fontFamily: font, fontSize: 13,
                  textAlign: "left", cursor: "pointer",
                  border: reason === r ? "2px solid #163244" : "1.5px solid rgba(0,0,0,0.12)",
                  background: reason === r ? "rgba(22,50,68,0.06)" : "#fff",
                  color: "#163244", fontWeight: reason === r ? 700 : 400,
                }}
              >
                {reason === r ? "● " : "○ "}{r}
              </button>
            ))}
          </div>
        </div>

        {/* Descripción */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontFamily: font, fontSize: 12, fontWeight: 700, color: "#163244", display: "block", marginBottom: 6 }}>
            Descripción adicional <span style={{ fontWeight: 400, color: "#aab8c2" }}>(opcional)</span>
          </label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={3}
            placeholder="Explicá en detalle qué ocurrió..."
            style={{
              width: "100%", fontFamily: font, fontSize: 13, padding: "10px 12px",
              borderRadius: 8, border: "1.5px solid rgba(22,50,68,0.15)",
              color: "#163244", resize: "vertical", outline: "none", boxSizing: "border-box",
            }}
          />
        </div>

        {error && (
          <div style={{ background: "rgba(192,57,43,0.08)", border: "1px solid rgba(192,57,43,0.2)", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#c0392b", fontFamily: font }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} disabled={loading} style={{ flex: 1, padding: "12px", borderRadius: 50, border: "2px solid rgba(0,0,0,0.1)", background: "transparent", color: "#7a93a4", fontFamily: font, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
            Cancelar
          </button>
          <button onClick={handleSubmit} disabled={loading} style={{ flex: 2, padding: "12px", borderRadius: 50, border: "none", background: loading ? "#dce5ec" : "#163244", color: loading ? "#7a93a4" : "#fff", fontFamily: font, fontWeight: 700, fontSize: 13, cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            {loading
              ? <><span style={{ width: 13, height: 13, border: "2px solid #7a93a4", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} /> Enviando...</>
              : "Enviar reclamo →"
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Agenda Widget ────────────────────────────────────────────────────────────
const DAYS_ES   = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const MONTHS_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const HOURS     = [8,9,10,11,12,13,14,15,16,17,18];

// Paleta de colores por servicio
const SERVICE_COLORS = {
  aire_acondicionado: { bg: "#e8f4fd", border: "#2196f3", text: "#1565c0" },
  albanileria:        { bg: "#fce4ec", border: "#e91e63", text: "#880e4f" },
  arquitectura:       { bg: "#f3e5f5", border: "#9c27b0", text: "#4a148c" },
  belleza_personal:   { bg: "#fce4ec", border: "#f06292", text: "#880e4f" },
  carpinteria:        { bg: "#fff3e0", border: "#ff9800", text: "#e65100" },
  cerrajeria:         { bg: "#eceff1", border: "#607d8b", text: "#263238" },
  control_plagas:     { bg: "#f1f8e9", border: "#8bc34a", text: "#33691e" },
  cuidado_mayores:    { bg: "#e8eaf6", border: "#5c6bc0", text: "#1a237e" },
  cuidado_ninos:      { bg: "#fce4ec", border: "#f48fb1", text: "#880e4f" },
  decoradores:        { bg: "#f3e5f5", border: "#ce93d8", text: "#4a148c" },
  electricista:       { bg: "rgba(231,164,50,0.12)", border: "#E7A432", text: "#b8820d" },
  entrenamiento:      { bg: "#e8f5e9", border: "#66bb6a", text: "#1b5e20" },
  gasista:            { bg: "#fff8e1", border: "#ffca28", text: "#f57f17" },
  herrero:            { bg: "#eceff1", border: "#90a4ae", text: "#263238" },
  infantiles:         { bg: "#fff9c4", border: "#ffee58", text: "#f57f17" },
  instalador_general: { bg: "#e0f2f1", border: "#26a69a", text: "#004d40" },
  jardinero:          { bg: "#e8f5e9", border: "#4caf50", text: "#1b5e20" },
  mascotas:           { bg: "#fff3e0", border: "#ffb74d", text: "#e65100" },
  mecanica_automotriz:{ bg: "#eceff1", border: "#78909c", text: "#263238" },
  mudanzas:           { bg: "#e3f2fd", border: "#42a5f5", text: "#0d47a1" },
  piletas:            { bg: "#e1f5fe", border: "#29b6f6", text: "#01579b" },
  pintores:           { bg: "#fce4ec", border: "#ec407a", text: "#880e4f" },
  plomeros:           { bg: "rgba(22,50,68,0.08)", border: "#163244", text: "#163244" },
  seguridad:          { bg: "#ffebee", border: "#ef5350", text: "#b71c1c" },
  servicio_domestico: { bg: "#e8eaf6", border: "#7986cb", text: "#1a237e" },
  tapicero:           { bg: "#efebe9", border: "#a1887f", text: "#3e2723" },
  tecnologia:         { bg: "#e8eaf6", border: "#5c6bc0", text: "#1a237e" },
};

// Convierte solicitudes con fecha asignada en eventos de agenda
function buildEvents(requests) {
  return requests
    .filter(r => ["assigned","in_progress","completed"].includes(r.status) && r.scheduled_date)
    .map(r => {
      const date = fechaLocal(r.scheduled_date); // fecha de calendario (inicio del presupuesto aceptado)
      const svcColor = SERVICE_COLORS[r.service_key] || { bg: "rgba(22,50,68,0.08)", border: "#163244", text: "#163244" };
      return {
        id:       r.id,
        title:    SERVICE_LABELS[r.service_key] || r.service_key,
        icon:     SERVICE_ICONS[r.service_key] || "🛠️",
        date,
        hour:     date.getHours() || 9,
        duration: r.duration_hours || 2,
        status:   r.status,
        provider: r.provider_name || "",
        color:    svcColor,
      };
    });
}

// Pill pequeña de evento (vista mes)
function EventPill({ ev }) {
  const c = ev.color;
  return (
    <div style={{
      background: c.bg, borderLeft: `2px solid ${c.border}`,
      borderRadius: 3, padding: "2px 5px",
      fontSize: 10, color: c.text, fontWeight: 600,
      marginBottom: 2, whiteSpace: "nowrap",
      overflow: "hidden", textOverflow: "ellipsis",
    }}>
      {ev.hour}:00 {ev.title}
    </div>
  );
}

// Bloque de evento (vista semana/día)
function EventBlock({ ev, style, detail }) {
  const c = ev.color;
  return (
    <div style={{
      position: "absolute", left: 3, right: 3,
      background: c.bg, border: `1px solid ${c.border}`,
      borderRadius: 8, padding: detail ? "8px 12px" : "3px 6px",
      overflow: "hidden", cursor: "pointer",
      ...style,
    }}>
      <div style={{ fontSize: detail ? 12 : 10, fontWeight: 700, color: c.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {ev.icon} {ev.title}
      </div>
      {detail ? (
        <>
          <div style={{ fontSize: 10, color: c.text, opacity: 0.7, marginTop: 2 }}>{ev.hour}:00 – {ev.hour + ev.duration}:00{ev.provider && ` · ${ev.provider}`}</div>
          <div style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 4, background: "rgba(255,255,255,0.2)", borderRadius: 20, padding: "2px 8px", fontSize: 9, fontWeight: 700, color: c.text }}>
            {STATUS_CONFIG[ev.status]?.label || ev.status}
          </div>
        </>
      ) : (
        <div style={{ fontSize: 9, color: c.text, opacity: 0.7 }}>{ev.hour}:00</div>
      )}
    </div>
  );
}

function AgendaWidget({ requests }) {
  const [view,    setView]    = useState("month");
  const [current, setCurrent] = useState(new Date());

  const events = useMemo(() => buildEvents(requests), [requests]);

  const navPeriod = (dir) => {
    const d = new Date(current);
    if (view === "month") d.setMonth(d.getMonth() + dir);
    else d.setDate(d.getDate() + dir);
    setCurrent(d);
  };

  const today    = new Date();
  const isToday  = (d) => d.toDateString() === today.toDateString();
  const eventsOn = (date) => events.filter(e => e.date.toDateString() === date.toDateString());

  const periodLabel = () => {
    if (view === "month") return `${MONTHS_ES[current.getMonth()]} ${current.getFullYear()}`;
    return `${DAYS_ES[current.getDay()]} ${current.getDate()} ${MONTHS_ES[current.getMonth()]}`;
  };

  // ── VISTA MES — compacta para 290px ───────────────────────────────────────
  // Días abreviados a 1 letra para que entren en 290px
  const DAYS_SHORT = ["D","L","M","X","J","V","S"];

  const MonthView = () => {
    const y = current.getFullYear(), m = current.getMonth();
    const firstDay    = new Date(y, m, 1).getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push({ date: new Date(y, m, -firstDay + i + 1), other: true });
    for (let i = 1; i <= daysInMonth; i++) cells.push({ date: new Date(y, m, i), other: false });
    while (cells.length % 7 !== 0) {
      const last = cells[cells.length - 1].date;
      cells.push({ date: new Date(last.getTime() + 86400000), other: true });
    }

    return (
      <div>
        {/* Cabecera días */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", borderBottom: "1px solid rgba(0,0,0,0.05)", padding: "0 6px" }}>
          {DAYS_SHORT.map(d => (
            <div key={d} style={{ padding: "7px 0", textAlign: "center", fontSize: 10, fontWeight: 700, color: "#aab8c2" }}>{d}</div>
          ))}
        </div>
        {/* Celdas */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", padding: "4px 6px 6px" }}>
          {cells.map(({ date, other }, i) => {
            const evs = eventsOn(date);
            const hasEv = evs.length > 0;
            return (
              <div
                key={i}
                onClick={() => { if (!other && hasEv) { setCurrent(date); setView("day"); } }}
                style={{
                  display: "flex", flexDirection: "column", alignItems: "center",
                  padding: "3px 0", opacity: other ? 0.3 : 1,
                  cursor: !other && hasEv ? "pointer" : "default",
                }}
              >
                <div style={{
                  width: 26, height: 26, borderRadius: "50%",
                  background: isToday(date) ? "#163244" : hasEv && !other ? "#E7A432" : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontWeight: isToday(date) || hasEv ? 800 : 400,
                  color: isToday(date) ? "#fff" : hasEv && !other ? "#163244" : "#5a6e7f",
                  transition: "all 0.15s",
                }}>
                  {date.getDate()}
                </div>
                {/* Dot si hay evento y no es el círculo amarillo */}
                {hasEv && !other && !isToday(date) && (
                  <div style={{ width: 4, height: 4, borderRadius: "50%", background: evs[0].color.border, marginTop: 1 }} />
                )}
              </div>
            );
          })}
        </div>
        {/* Próximos eventos del mes */}
        {events.filter(e => {
          const em = e.date.getMonth(), ey = e.date.getFullYear();
          return em === current.getMonth() && ey === current.getFullYear();
        }).sort((a, b) => a.date - b.date).slice(0, 3).map((ev, i) => (
          <div
            key={i}
            onClick={() => { setCurrent(ev.date); setView("day"); }}
            style={{
              display: "flex", alignItems: "center", gap: 9, padding: "7px 10px",
              borderTop: "1px solid rgba(0,0,0,0.05)", cursor: "pointer",
              background: "transparent", transition: "background 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "#f5f7fa"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
          >
            <div style={{ width: 3, alignSelf: "stretch", borderRadius: 2, background: ev.color.border, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#163244", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {ev.icon} {ev.title}
              </div>
              <div style={{ fontSize: 10, color: "#7a93a4", marginTop: 1 }}>
                {ev.date.getDate()} {MONTHS_ES[ev.date.getMonth()].slice(0,3)} · {ev.hour}:00
              </div>
            </div>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#aab8c2" strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
          </div>
        ))}
      </div>
    );
  };

  // ── VISTA DÍA — detallada, cabe en 290px ─────────────────────────────────
  const DayView = () => {
    const evs    = eventsOn(current);
    const SLOT_H = 44;
    return (
      <div>
        {/* Sub-header día */}
        <div style={{ padding: "10px 14px 10px", borderBottom: "1px solid rgba(0,0,0,0.05)", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: isToday(current) ? "#163244" : "#f0f5f8", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <span style={{ fontSize: 18, fontWeight: 900, color: isToday(current) ? "#E7A432" : "#163244", lineHeight: 1 }}>{current.getDate()}</span>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#163244" }}>{DAYS_ES[current.getDay()]}, {MONTHS_ES[current.getMonth()].slice(0,3)} {current.getFullYear()}</div>
            <div style={{ fontSize: 10, color: "#7a93a4", marginTop: 1 }}>
              {evs.length === 0 ? "Sin servicios" : `${evs.length} servicio${evs.length !== 1 ? "s" : ""}`}
            </div>
          </div>
        </div>

        {evs.length === 0 ? (
          <div style={{ padding: "24px 14px", textAlign: "center", color: "#aab8c2" }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" style={{ marginBottom: 6 }}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <div style={{ fontSize: 11, fontWeight: 600 }}>Sin servicios este día</div>
          </div>
        ) : (
          <div style={{ padding: "8px 10px", display: "flex", flexDirection: "column", gap: 8 }}>
            {evs.map((ev, j) => {
              const c = ev.color;
              return (
                <div key={j} style={{
                  background: c.bg, border: `1px solid ${c.border}`,
                  borderRadius: 10, padding: "10px 12px",
                }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: c.text, marginBottom: 4 }}>{ev.icon} {ev.title}</div>
                  <div style={{ fontSize: 11, color: c.text, opacity: 0.75, marginBottom: 6 }}>
                    {ev.hour}:00 – {ev.hour + ev.duration}:00
                    {ev.provider && <span> · {ev.provider}</span>}
                  </div>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "rgba(255,255,255,0.55)", borderRadius: 20, padding: "2px 8px", fontSize: 10, fontWeight: 700, color: c.text }}>
                    {STATUS_CONFIG[ev.status]?.label || ev.status}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Grilla horaria compacta — solo si hay eventos */}
        {evs.length > 0 && (
          <div style={{ padding: "0 10px 8px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "36px 1fr", borderRadius: 8, overflow: "hidden", border: "1px solid rgba(0,0,0,0.06)" }}>
              <div style={{ borderRight: "1px solid rgba(0,0,0,0.05)", background: "#fafbfc" }}>
                {HOURS.map(h => <div key={h} style={{ height: SLOT_H, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 5, fontSize: 9, color: "#bcc8d0", borderBottom: "1px solid rgba(0,0,0,0.04)" }}>{h}</div>)}
              </div>
              <div style={{ position: "relative" }}>
                {HOURS.map(h => <div key={h} style={{ height: SLOT_H, borderBottom: "1px solid rgba(0,0,0,0.04)" }} />)}
                {evs.map((ev, j) => {
                  const top    = (ev.hour - HOURS[0]) * SLOT_H + 3;
                  const height = Math.max(ev.duration * SLOT_H - 6, SLOT_H - 6);
                  const c      = ev.color;
                  return (
                    <div key={j} style={{ position: "absolute", left: 3, right: 3, top, height, background: c.bg, border: `1px solid ${c.border}`, borderRadius: 6, padding: "3px 6px", overflow: "hidden" }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: c.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ev.icon} {ev.title}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid rgba(0,0,0,0.07)", overflow: "hidden", fontFamily: font }}>
      {/* Header compacto — todo en una fila */}
      <div style={{ padding: "10px 12px", borderBottom: "1px solid rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: 6 }}>
        {/* Prev */}
        <button onClick={() => navPeriod(-1)} style={{ width: 26, height: 26, borderRadius: 7, border: "1px solid rgba(0,0,0,0.1)", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#163244", flexShrink: 0 }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        {/* Label período */}
        <span style={{ fontSize: 12, fontWeight: 700, color: "#163244", flex: 1, textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{periodLabel()}</span>
        {/* Next */}
        <button onClick={() => navPeriod(1)} style={{ width: 26, height: 26, borderRadius: 7, border: "1px solid rgba(0,0,0,0.1)", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#163244", flexShrink: 0 }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
        </button>
        {/* Hoy */}
        <button onClick={() => { setCurrent(new Date()); }} style={{ padding: "4px 9px", borderRadius: 6, border: "1px solid rgba(0,0,0,0.1)", background: "#fff", cursor: "pointer", fontSize: 10, fontWeight: 700, color: "#163244", fontFamily: font, flexShrink: 0 }}>Hoy</button>
        {/* Tabs vista */}
        <div style={{ display: "flex", background: "#f5f7fa", borderRadius: 7, padding: 2, gap: 1, flexShrink: 0 }}>
          {[["month","Mes"],["day","Día"]].map(([v, lbl]) => (
            <button key={v} onClick={() => setView(v)} style={{ padding: "4px 8px", borderRadius: 5, border: "none", background: view === v ? "#163244" : "transparent", color: view === v ? "#fff" : "#7a93a4", fontFamily: font, fontWeight: 700, fontSize: 10, cursor: "pointer", transition: "all 0.15s" }}>
              {lbl}
            </button>
          ))}
        </div>
      </div>

      {/* Contenido */}
      {view === "month" && <MonthView />}
      {view === "day"   && <DayView />}
    </div>
  );
}

// ─── Progress Bar ─────────────────────────────────────────────────────────────
function ProgressBar({ status }) {
  const step  = STATUS_CONFIG[status]?.step ?? 0;
  const steps = ["Creada", "En búsqueda", "Presupuestos", "Asignada", "Completada"];
  if (step < 0) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
      {steps.map((s, i) => {
        const done = i < step, active = i === step, last = i === steps.length - 1;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", flex: last ? 0 : 1 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
              <div style={{
                width: 18, height: 18, borderRadius: "50%",
                background: done ? "#E7A432" : active ? "#163244" : "#dce5ec",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 8, color: done || active ? "#fff" : "#aab8c2", fontWeight: 800,
              }}>{done ? "✓" : i + 1}</div>
              <span style={{ fontSize: 8, fontWeight: active ? 700 : 400, color: active ? "#163244" : done ? "#E7A432" : "#aab8c2", whiteSpace: "nowrap" }}>{s}</span>
            </div>
            {!last && <div style={{ flex: 1, height: 2, marginBottom: 12, background: done ? "#E7A432" : "#dce5ec", minWidth: 12 }} />}
          </div>
        );
      })}
    </div>
  );
}

// ─── Request Card ─────────────────────────────────────────────────────────────
function RequestCard({ req, sinResultados, onViewQuotes, onRate, onChat, onDispute, onCancel, onSearchAgain }) {
  const [confirmarCancelar, setConfirmarCancelar] = useState(false);
  const [cancelando,        setCancelando]        = useState(false);
  const [buscando,          setBuscando]          = useState(false);
  // sinResultados ("Buscar otro" no encontró a nadie) viene de la página y no
  // de acá: la búsqueda recarga la lista, el spinner desmonta las cards y un
  // estado propio se perdía (prueba en dev, 26-10-2026).
  // El prestador asignado canceló el trabajo y la solicitud volvió a buscar
  // (backend providerCalendar.js, cancelarTrabajo): aviso, "Buscar otro
  // prestador" y "Cancelar solicitud". El backend ya borró al prestador.
  const prestadorCancelo = Boolean(req.prestador_cancelo_at) && ["waiting_assignment", "waiting_quotes"].includes(req.status);
  const cfg         = prestadorCancelo
    ? { ...STATUS_CONFIG.waiting_quotes, label: "Sin prestador", color: "#c0392b", bg: "rgba(192,57,43,0.1)" }
    : (STATUS_CONFIG[req.status] || STATUS_CONFIG.created);
  const icon        = SERVICE_ICONS[req.service_key] || "🛠️";
  const label       = SERVICE_LABELS[req.service_key] || req.service_key;
  const isQuoted    = req.status === "quoted";
  const isCompleted = req.status === "completed";
  const isCancelled = ["cancelled", "expired"].includes(req.status);
  const isWaiting   = ["waiting_assignment", "waiting_quotes"].includes(req.status);
  // "created" quiere decir que la búsqueda nunca corrió bien: runMatching pasa
  // la solicitud a waiting_* en cuanto anda. Antes la tarjeta quedaba "Creada"
  // sin ningún botón, ni siquiera cancelar (26-10-2026).
  const sinBusqueda = req.status === "created";
  const puedeCancelar = isWaiting || sinBusqueda;
  // La búsqueda corrió pero nadie la tiene viva: sin matches vigentes y sin
  // presupuestos (backend /my, 26-10-2026). Pasa cuando el único prestador
  // del rubro está sin disponibilidad, fuera de zona o con el tope lleno.
  // Es el estado actual, no un momento: sobrevive a un F5. === 0 para que un
  // backend sin los conteos no lo dispare.
  const sinCandidatos = isWaiting && !prestadorCancelo && req.matches_count === 0 && req.quotes_count === 0;
  const hasChat     = ["assigned", "in_progress", "completed"].includes(req.status);

  // Disputa: solo en paid/completed con transaction_id y dentro de 7 días
  const canDispute = req.transaction_id && ["paid", "completed", "assigned", "in_progress"].includes(req.status) && !req.has_dispute &&
    ((Date.now() - new Date(req.paid_at || req.created_at)) / (1000 * 60 * 60 * 24)) <= 7;

  const commonKeys = ["titulo","descripcion","urgencia","presupuesto_estimado","acepta_visita","acepta_presupuesto_previo","terminos_aceptados"];
  // Sin valor no se muestra: antes un adjunto no cargado salía como "ADJUNTOS: null"
  const sinValor = (v) => v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0);
  const specificData = req.specific_data
    ? Object.entries(req.specific_data).filter(([k, v]) => !commonKeys.includes(k) && !sinValor(v)).slice(0, 4)
    : [];

  const chipStyle = (variant) => ({
    display: "inline-flex", alignItems: "center", gap: 4,
    background: variant === "amber" ? "rgba(231,164,50,0.1)" : variant === "green" ? "rgba(27,127,78,0.08)" : "rgba(22,50,68,0.06)",
    border: `1px solid ${variant === "amber" ? "rgba(231,164,50,0.3)" : variant === "green" ? "rgba(27,127,78,0.2)" : "rgba(22,50,68,0.12)"}`,
    borderRadius: 7, padding: "3px 9px", fontSize: 11,
    color: variant === "amber" ? "#b8820d" : variant === "green" ? "#1b7f4e" : "#163244",
    fontWeight: 500,
  });

  return (
    <div style={{
      background: "#fff", borderRadius: 14, marginBottom: 12, fontFamily: font,
      border: `1.5px solid ${isQuoted ? "#E7A432" : isCancelled ? "rgba(192,57,43,0.2)" : "rgba(0,0,0,0.07)"}`,
      overflow: "hidden", opacity: isCancelled ? 0.7 : 1,
    }}>
      {isQuoted && (
        <div style={{ background: "#E7A432", padding: "5px 18px", fontSize: 10, fontWeight: 800, color: "#163244", letterSpacing: "0.06em", display: "flex", alignItems: "center", gap: 5 }}>
          ⭐ PRESUPUESTOS LISTOS — Revisalos ahora
        </div>
      )}
      <div style={{ padding: "16px 18px" }}>
        {/* Top */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 42, height: 42, borderRadius: 10, background: isCancelled ? "#f0f5f8" : "#163244", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>{icon}</div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 14, color: "#163244" }}>{label}</div>
              <div style={{ fontSize: 11, color: "#7a93a4", marginTop: 2 }}>
                {timeAgo(req.created_at)}{req.common_data?.ubicacion?.ciudad && ` · ${req.common_data.ubicacion.ciudad}`}
              </div>
            </div>
          </div>
          <span style={{ padding: "3px 10px", borderRadius: 50, fontSize: 10, fontWeight: 700, color: cfg.color, background: cfg.bg, whiteSpace: "nowrap" }}>
            {cfg.label.toUpperCase()}
          </span>
        </div>

        {/* Chips */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 }}>
          {req.common_data?.urgencia && (
            <span style={chipStyle(req.common_data.urgencia.includes("Urgente") ? "amber" : "dark")}>
              <IconClock /> {req.common_data.urgencia}
            </span>
          )}
          {req.common_data?.ubicacion?.direccion && (
            <span style={chipStyle("dark")}>
              <IconPin /> {req.common_data.ubicacion.direccion}{req.common_data.ubicacion.ciudad && `, ${req.common_data.ubicacion.ciudad}`}
            </span>
          )}
          {req.common_data?.presupuesto_estimado && (
            <span style={chipStyle("dark")}>
              <IconDollar /> $ {Number(req.common_data.presupuesto_estimado).toLocaleString("es-AR")}
            </span>
          )}
          {isQuoted && req.quotes_count > 0 && (
            <span style={chipStyle("green")}><IconCheck /> {req.quotes_count} {req.quotes_count === 1 ? "presupuesto recibido" : "presupuestos recibidos"}</span>
          )}
        </div>

        {/* Título */}
        {req.common_data?.titulo && (
          <div style={{ fontSize: 12, color: "#163244", fontWeight: 600, marginBottom: 8 }}>"{req.common_data.titulo}"</div>
        )}

        {/* Descripción */}
        {req.common_data?.descripcion && (
          <div style={{ background: "#f8fafb", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#5a6e7f", lineHeight: 1.6, borderLeft: "3px solid rgba(22,50,68,0.15)", marginBottom: 10, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
            {req.common_data.descripcion}
          </div>
        )}

        {/* Datos específicos */}
        {specificData.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 6, marginBottom: 10 }}>
            {specificData.map(([k, v]) => (
              <div key={k} style={{ background: "#f5f7fa", borderRadius: 8, padding: "7px 10px" }}>
                <div style={{ fontSize: 9, color: "#7a93a4", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>{k.replace(/_/g, " ")}</div>
                <div style={{ fontSize: 12, color: "#163244", fontWeight: 700 }}>{formatValue(v)}</div>
              </div>
            ))}
          </div>
        )}

        {/* Prestador asignado */}
        {req.provider_name && ["assigned","in_progress","completed"].includes(req.status) && (
          <div style={{ background: "#f8fafb", borderRadius: 9, padding: "9px 12px", marginBottom: 10, border: "1px solid rgba(0,0,0,0.07)", display: "flex", alignItems: "center", gap: 9 }}>
            <div style={{ width: 30, height: 30, borderRadius: "50%", background: "#163244", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 11, color: "#E7A432", flexShrink: 0 }}>{req.provider_name[0]}</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 12, color: "#163244" }}>{req.provider_name}</div>
              {req.provider_rating && (
                <div style={{ fontSize: 11, color: "#7a93a4" }}>
                  {"★".repeat(Math.round(parseFloat(req.provider_rating)))}{"☆".repeat(5 - Math.round(parseFloat(req.provider_rating)))} {parseFloat(req.provider_rating).toFixed(1)} · {req.provider_jobs || 0} trabajos
                </div>
              )}
            </div>
            <div style={{ marginLeft: "auto", background: "rgba(27,127,78,0.1)", color: "#1b7f4e", borderRadius: 50, padding: "2px 9px", fontSize: 10, fontWeight: 700 }}>Asignado</div>
          </div>
        )}

        {/* Contacto */}
        {["assigned","in_progress"].includes(req.status) && (req.provider_phone || req.provider_email) && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 10 }}>
            {req.provider_phone && <a href={`tel:${req.provider_phone}`} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 8, background: "#163244", color: "#fff", fontSize: 11, fontWeight: 700, textDecoration: "none" }}>📱 {req.provider_phone}</a>}
            {req.provider_email && <a href={`mailto:${req.provider_email}`} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 8, border: "1.5px solid #163244", background: "#fff", color: "#163244", fontSize: 11, fontWeight: 700, textDecoration: "none" }}>✉️ {req.provider_email}</a>}
          </div>
        )}

        {prestadorCancelo && (
          <div style={{ marginBottom: 10, padding: "10px 14px", borderRadius: 10, background: "rgba(192,57,43,0.06)", border: "1px solid rgba(192,57,43,0.25)", color: "#c0392b", fontSize: 12, fontWeight: 600 }}>
            El prestador canceló el trabajo. Podés buscar otro prestador o cancelar la solicitud.
            {sinResultados && (
              <div style={{ marginTop: 6, fontWeight: 500 }}>
                Buscamos y no hay otro prestador disponible para este servicio en este momento. Podés volver a intentar más tarde o cancelar la solicitud.
              </div>
            )}
          </div>
        )}

        {sinBusqueda && (
          <div style={{ marginBottom: 10, padding: "10px 14px", borderRadius: 10, background: "rgba(192,57,43,0.06)", border: "1px solid rgba(192,57,43,0.25)", color: "#c0392b", fontSize: 12, fontWeight: 600 }}>
            No pudimos buscar prestadores para tu solicitud. Podés volver a intentar la búsqueda o cancelar la solicitud.
          </div>
        )}

        {sinCandidatos && (
          <div style={{ marginBottom: 10, padding: "10px 14px", borderRadius: 10, background: "rgba(192,57,43,0.06)", border: "1px solid rgba(192,57,43,0.25)", color: "#c0392b", fontSize: 12, fontWeight: 600 }}>
            No encontramos prestadores disponibles para esta solicitud. Podés volver a intentar la búsqueda más tarde o cancelar la solicitud.
          </div>
        )}

        <ProgressBar status={req.status} />

        {/* Acciones */}
        <div style={{ display: "flex", gap: 8 }}>
          {isQuoted && <button onClick={() => onViewQuotes(req.id)} style={{ flex: 1, padding: "10px", borderRadius: 50, border: "none", background: "#163244", color: "#fff", fontFamily: font, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Ver presupuestos →</button>}
          {hasChat && <button onClick={() => onChat(req.id)} style={{ flex: 1, padding: "9px", borderRadius: 50, border: "1.5px solid #163244", background: "#fff", color: "#163244", fontFamily: font, fontWeight: 700, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}><IconChat /> Chat</button>}
          {isCompleted && !req.rated && <button onClick={() => onRate(req.id)} style={{ flex: 1, padding: "9px", borderRadius: 50, border: "1.5px solid #E7A432", background: "rgba(231,164,50,0.06)", color: "#163244", fontFamily: font, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>⭐ Calificar</button>}
          {isCompleted && req.rated && <div style={{ flex: 1, padding: "9px", borderRadius: 50, background: "#e8f7ef", border: "1px solid #1b7f4e", color: "#1b7f4e", fontSize: 12, fontWeight: 600, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}><IconCheck /> Ya calificaste</div>}
          {(prestadorCancelo || sinBusqueda || sinCandidatos) && (
            <button
              onClick={async () => { setBuscando(true); await onSearchAgain(req.id); setBuscando(false); }}
              disabled={buscando}
              style={{ flex: 1, padding: "10px", borderRadius: 50, border: "none", background: "#163244", color: "#fff", fontFamily: font, fontWeight: 700, fontSize: 13, cursor: buscando ? "not-allowed" : "pointer", opacity: buscando ? 0.7 : 1 }}>
              {buscando ? "Buscando…" : (sinBusqueda || sinCandidatos) ? "Reintentar búsqueda" : "Buscar otro prestador"}
            </button>
          )}
          {isWaiting && !prestadorCancelo && !sinCandidatos && <div style={{ flex: 1, padding: "9px", borderRadius: 50, background: "#f8fafb", border: "1px solid rgba(0,0,0,0.08)", color: "#7a93a4", fontSize: 12, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>Buscando prestadores...</div>}
          {puedeCancelar && !confirmarCancelar && (
            <button onClick={() => setConfirmarCancelar(true)} style={{ padding: "9px 16px", borderRadius: 50, border: "1.5px solid rgba(0,0,0,0.12)", background: "transparent", color: "#7a93a4", fontFamily: font, fontWeight: 600, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}>
              Cancelar solicitud
            </button>
          )}
          {canDispute && (
            <button onClick={() => onDispute(req)} style={{ padding: "9px 16px", borderRadius: 50, border: "1.5px solid rgba(192,57,43,0.3)", background: "rgba(192,57,43,0.05)", color: "#c0392b", fontFamily: font, fontWeight: 700, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}>
              ⚑ Reclamar
            </button>
          )}
        </div>

        {/* Sin prestador asignado la cancelación es sin cargo (payments.js, /cancel). */}
        {puedeCancelar && confirmarCancelar && (
          <div style={{ marginTop: 10, padding: "12px 14px", borderRadius: 10, background: "#f8fafb", border: "1px solid rgba(0,0,0,0.08)" }}>
            <div style={{ fontSize: 12, color: "#163244", fontWeight: 600, marginBottom: 10 }}>
              {prestadorCancelo
                ? "¿Cancelar esta solicitud? No tiene costo."
                : "¿Cancelar esta solicitud? No tiene costo: todavía no contrataste a nadie."}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setConfirmarCancelar(false)} disabled={cancelando} style={{ flex: 1, padding: "9px", borderRadius: 50, border: "1.5px solid rgba(0,0,0,0.12)", background: "#fff", color: "#7a93a4", fontFamily: font, fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
                No, seguir esperando
              </button>
              <button
                onClick={async () => { setCancelando(true); const ok = await onCancel(req.id); setCancelando(false); if (!ok) setConfirmarCancelar(false); }}
                disabled={cancelando}
                style={{ flex: 1, padding: "9px", borderRadius: 50, border: "none", background: "#c0392b", color: "#fff", fontFamily: font, fontWeight: 700, fontSize: 12, cursor: cancelando ? "not-allowed" : "pointer", opacity: cancelando ? 0.7 : 1 }}>
                {cancelando ? "Cancelando…" : "Sí, cancelar"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────
function Sidebar({ requests, navigate }) {
  const [agendaOpen, setAgendaOpen] = useState(true);
  const scheduledCount = requests.filter(r => ["assigned","in_progress"].includes(r.status) && r.scheduled_date).length;

  const total     = requests.length;
  const quoted    = requests.filter(r => r.status === "quoted").length;
  const active    = requests.filter(r => ["created","waiting_assignment","waiting_quotes","assigned","in_progress"].includes(r.status)).length;
  const completed = requests.filter(r => r.status === "completed").length;

  const catCount = {};
  requests.forEach(r => { const l = SERVICE_LABELS[r.service_key] || r.service_key; catCount[l] = (catCount[l] || 0) + 1; });
  const topCats   = Object.entries(catCount).sort((a, b) => b[1] - a[1]).slice(0, 4);
  const catColors = ["#163244", "#E7A432", "rgba(22,50,68,0.35)", "rgba(231,164,50,0.4)"];

  const activity = [...requests]
    .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
    .slice(0, 4)
    .map(r => ({ label: SERVICE_LABELS[r.service_key] || r.service_key, status: STATUS_CONFIG[r.status]?.label || r.status, time: timeAgo(r.updated_at || r.created_at), color: STATUS_CONFIG[r.status]?.color || "#5c7a8a" }));

  const donutR = 28, donutC = 36, circ = 2 * Math.PI * donutR;
  let off = 0;
  const segs = topCats.map(([, c], i) => { const d = (c / (total || 1)) * circ; const s = { d, off, col: catColors[i] }; off += d; return s; });

  const sCard  = { background: "#fff", borderRadius: 14, border: "1px solid rgba(0,0,0,0.07)", overflow: "hidden", marginBottom: 14, fontFamily: font };
  const sHdr   = { padding: "14px 16px 0", display: "flex", alignItems: "center", gap: 8, marginBottom: 12 };
  const sAcc   = { width: 3, height: 13, background: "#E7A432", borderRadius: 2, flexShrink: 0 };
  const sTit   = { fontSize: 11, fontWeight: 700, color: "#163244", textTransform: "uppercase", letterSpacing: 1 };

  return (
    <div style={{ fontFamily: font }}>

      {/* Agenda colapsable */}
      <div style={{ ...sCard, marginBottom: 14 }}>
        <button
          onClick={() => setAgendaOpen(o => !o)}
          style={{
            width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "transparent", border: "none", padding: "14px 16px",
            cursor: "pointer", fontFamily: font,
            borderBottom: agendaOpen ? "1px solid rgba(0,0,0,0.06)" : "none",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <div style={sAcc}/>
            <span style={sTit}>Mi agenda</span>
            {scheduledCount > 0 && (
              <span style={{ background: "#E7A432", color: "#163244", borderRadius: 50, padding: "1px 8px", fontSize: 10, fontWeight: 800 }}>
                {scheduledCount}
              </span>
            )}
          </div>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7a93a4" strokeWidth="2.5" strokeLinecap="round"
            style={{ transform: agendaOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", flexShrink: 0 }}>
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </button>
        {agendaOpen && <AgendaWidget requests={requests} />}
      </div>

      {/* Resumen */}
      <div style={sCard}>
        <div style={sHdr}><div style={sAcc}/><span style={sTit}>Resumen</span></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, padding: "0 16px 16px" }}>
          {[
            { n: total,     l: "Total",          color: "#163244", pct: 100 },
            { n: quoted,    l: "Con presupuestos",color: "#E7A432", pct: total ? (quoted/total)*100 : 0 },
            { n: active,    l: "En búsqueda",    color: "#b06a00", pct: total ? (active/total)*100 : 0 },
            { n: completed, l: "Completadas",    color: "#1b7f4e", pct: total ? (completed/total)*100 : 0 },
          ].map(({ n, l, color, pct }) => (
            <div key={l} style={{ background: "#f5f7fa", borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ fontSize: 22, fontWeight: 900, color, lineHeight: 1 }}>{n}</div>
              <div style={{ fontSize: 10, color: "#7a93a4", marginTop: 3, fontWeight: 500, lineHeight: 1.3 }}>{l}</div>
              <div style={{ height: 3, borderRadius: 2, marginTop: 8, background: "#e8eef2", overflow: "hidden" }}>
                <div style={{ height: "100%", borderRadius: 2, background: color, width: `${pct}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Por categoría */}
      {topCats.length > 0 && (
        <div style={sCard}>
          <div style={sHdr}><div style={sAcc}/><span style={sTit}>Por categoría</span></div>
          <div style={{ padding: "0 16px 16px", display: "flex", alignItems: "center", gap: 16 }}>
            <svg width={donutC*2} height={donutC*2} style={{ flexShrink: 0 }}>
              <circle cx={donutC} cy={donutC} r={donutR} fill="none" stroke="#e8eef2" strokeWidth="10"/>
              {segs.map((s, i) => (
                <circle key={i} cx={donutC} cy={donutC} r={donutR} fill="none" stroke={s.col} strokeWidth="10"
                  strokeDasharray={`${s.d} ${circ - s.d}`} strokeDashoffset={-s.off + circ * 0.25}
                  transform={`rotate(-90 ${donutC} ${donutC})`}/>
              ))}
              <text x={donutC} y={donutC+5} textAnchor="middle" fontSize="13" fontWeight="800" fill="#163244" fontFamily={font}>{total}</text>
            </svg>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}>
              {topCats.map(([lbl, cnt], i) => (
                <div key={lbl} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: catColors[i], flexShrink: 0 }}/>
                  <span style={{ fontSize: 11, color: "#5a6e7f", flex: 1 }}>{lbl}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#163244" }}>{cnt}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Actividad reciente */}
      {activity.length > 0 && (
        <div style={sCard}>
          <div style={sHdr}><div style={sAcc}/><span style={sTit}>Actividad reciente</span></div>
          <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column" }}>
            {activity.map((a, i) => (
              <div key={i} style={{ display: "flex", gap: 12, padding: "9px 0", borderBottom: i < activity.length - 1 ? "1px solid rgba(0,0,0,0.05)" : "none" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: a.color, flexShrink: 0, marginTop: 4 }}/>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: "#5a6e7f", lineHeight: 1.5 }}>
                    <strong style={{ color: "#163244", fontWeight: 700 }}>{a.label}</strong> — {a.status}
                  </div>
                  <div style={{ fontSize: 10, color: "#aab8c2", marginTop: 1 }}>{a.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA dark */}
      <div style={{ background: "#163244", borderRadius: 14, overflow: "hidden", position: "relative", marginBottom: 14 }}>
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none", backgroundImage: "radial-gradient(circle at 85% 15%, rgba(231,164,50,0.12) 0%, transparent 55%), repeating-linear-gradient(45deg, transparent, transparent 40px, rgba(255,255,255,0.015) 40px, rgba(255,255,255,0.015) 41px)" }}/>
        <div style={{ padding: "18px 18px 20px", position: "relative", zIndex: 1 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>¿Necesitás algo más?</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#fff", marginBottom: 4 }}>Pedí un nuevo servicio</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", lineHeight: 1.5, marginBottom: 16 }}>Tenemos 27 categorías de servicios para ayudarte.</div>
          <button onClick={() => navigate("/?scroll=servicios")} style={{ width: "100%", background: "#E7A432", color: "#163244", border: "none", borderRadius: 50, padding: "10px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: font }}>
            Explorar servicios →
          </button>
        </div>
      </div>

      {/* Tip */}
      <div style={sCard}>
        <div style={{ padding: "14px 16px" }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: "rgba(231,164,50,0.1)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#E7A432" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#163244", marginBottom: 4 }}>Tip: respondé rápido</div>
          <div style={{ fontSize: 12, color: "#7a93a4", lineHeight: 1.55 }}>Los prestadores que reciben respuesta en menos de 2hs tienen un 40% más de chances de completar el trabajo exitosamente.</div>
        </div>
      </div>
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────
function EmptyState({ navigate }) {
  return (
    <div style={{ textAlign: "center", padding: "60px 20px", background: "#fff", borderRadius: 14, border: "2px dashed rgba(0,0,0,0.1)" }}>
      <div style={{ fontSize: 44, marginBottom: 16 }}>🛠️</div>
      <div style={{ fontWeight: 800, fontSize: 17, color: "#163244", marginBottom: 8 }}>Todavía no hiciste ninguna solicitud</div>
      <div style={{ color: "#7a93a4", fontSize: 13, marginBottom: 24 }}>Explorá los servicios disponibles y pedí tu primera solicitud.</div>
      <button onClick={() => navigate("/?scroll=servicios")} style={{ padding: "12px 30px", borderRadius: 50, background: "#163244", border: "none", color: "#fff", fontFamily: font, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
        Ver servicios →
      </button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ClientRequestsPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [requests,    setRequests]    = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadError,   setLoadError]   = useState("");
  const [filter,      setFilter]      = useState("all");
  const [page,        setPage]        = useState(1);
  const [disputeModal, setDisputeModal] = useState(null);
  const [toast,        setToast]        = useState(null);
  // Por id de solicitud: "Buscar otro" corrió y no encontró a nadie. Vive acá
  // para sobrevivir al fetchRequests que dispara la propia búsqueda. Un F5 lo
  // borra: el servidor no lo guarda.
  const [sinResultados, setSinResultados] = useState({});
  const PAGE_SIZE = 5;

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3500); };

  // "Buscar otro prestador": el matching con las coordenadas del navegador.
  // El servidor no las tiene guardadas (Pendientes,
  // MATCHING-ZONA-DESDE-EL-DOMICILIO-NO-DESDE-EL-TELEFONO). Sin permiso se
  // busca igual, como en el alta (ServiceRequestPage.jsx), y se avisa.
  const getCoords = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({ lat: null, lng: null });
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        ()    => resolve({ lat: null, lng: null }),
        { timeout: 5000 }
      );
    });

  const handleSearchAgain = async (id) => {
    const { lat, lng } = await getCoords();
    const avisoUbicacion = lat === null
      ? " No pudimos usar tu ubicación: puede que te contacten prestadores de otras zonas."
      : "";
    try {
      const { data } = await api.post(`/matching/run/${id}`, { client_lat: lat, client_lng: lng });
      const n = Array.isArray(data?.matches) ? data.matches.length : 0;
      showToast((n > 0
        ? `Le avisamos a ${n} prestador${n > 1 ? "es" : ""}. Sus presupuestos te llegan acá.`
        : "No hay otro prestador disponible para este servicio en este momento. Podés volver a intentar más tarde o cancelar la solicitud.")
        + avisoUbicacion);
      setSinResultados(m => ({ ...m, [id]: n === 0 }));
      fetchRequests();
      return n;
    } catch (err) {
      showToast(err.response?.data?.error || "No se pudo buscar otro prestador.");
      return null;
    }
  };

  const handleCancel = async (id) => {
    try {
      await api.post(`/payments/cancel/${id}`, {});
      setRequests(rs => rs.map(r => r.id === id ? { ...r, status: "cancelled" } : r));
      showToast("Solicitud cancelada.");
      return true;
    } catch (err) {
      showToast(err.response?.data?.error || "No se pudo cancelar la solicitud.");
      return false;
    }
  };

  const fetchRequests = () => {
    setLoadingData(true); setLoadError("");
    api.get("/api/service-requests/my")
      .then(res => setRequests(res.data.requests || res.data || []))
      .catch(err => { console.error(err); setLoadError("No se pudieron cargar tus solicitudes."); })
      .finally(() => setLoadingData(false));
  };

  useEffect(() => { fetchRequests(); }, [location.key]);

  const FILTERS = useMemo(() => [
    { id: "all",       label: "Todas",       count: requests.length },
    { id: "active",    label: "Activas",     count: requests.filter(r => ["created","waiting_assignment","waiting_quotes","quoted","assigned","in_progress"].includes(r.status)).length },
    { id: "completed", label: "Completadas", count: requests.filter(r => r.status === "completed").length },
    { id: "cancelled", label: "Canceladas",  count: requests.filter(r => ["cancelled","expired"].includes(r.status)).length },
  ], [requests]);

  const sorted = useMemo(() => {
    const filtered = requests.filter(r => {
      if (filter === "active")    return ["created","waiting_assignment","waiting_quotes","quoted","assigned","in_progress"].includes(r.status);
      if (filter === "completed") return r.status === "completed";
      if (filter === "cancelled") return ["cancelled","expired"].includes(r.status);
      return true;
    });
    return [...filtered].sort((a, b) => {
      if (a.status === "quoted" && b.status !== "quoted") return -1;
      if (b.status === "quoted" && a.status !== "quoted") return  1;
      return new Date(b.created_at) - new Date(a.created_at);
    });
  }, [requests, filter]);

  const pendingQuotes = requests.filter(r => r.status === "quoted").length;

  return (
    <>
      <div style={{ fontFamily: font, background: "#f5f7fa", minHeight: "100vh", padding: "28px 24px 60px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 290px", gap: 20, alignItems: "start" }}>

            {/* Columna izquierda */}
            <div>
              {/* Header */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <h1 style={{ margin: 0, fontSize: 21, fontWeight: 800, color: "#163244" }}>Mis solicitudes</h1>
                    {pendingQuotes > 0 && (
                      <span style={{ background: "#E7A432", color: "#163244", borderRadius: 50, padding: "3px 11px", fontSize: 12, fontWeight: 800 }}>
                        {pendingQuotes} con presupuestos
                      </span>
                    )}
                  </div>
                  <button onClick={() => navigate("/?scroll=servicios")} style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#163244", color: "#fff", border: "none", borderRadius: 50, padding: "9px 20px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: font }}>
                    <IconPlus /> Nueva solicitud
                  </button>
                </div>
                <p style={{ margin: 0, color: "#7a93a4", fontSize: 12 }}>Seguí el estado de cada solicitud y revisá los presupuestos recibidos.</p>
              </div>

              {/* Filtros */}
              {!loadingData && !loadError && requests.length > 0 && (
                <div style={{ display: "flex", gap: 5, marginBottom: 16, background: "#fff", borderRadius: 12, padding: 5, border: "1px solid rgba(0,0,0,0.07)" }}>
                  {FILTERS.map(f => (
                    <button key={f.id} onClick={() => { setFilter(f.id); setPage(1); }} style={{ flex: 1, padding: "8px 6px", borderRadius: 9, border: "none", background: filter === f.id ? "#163244" : "transparent", color: filter === f.id ? "#fff" : "#5a6e7f", fontFamily: font, fontWeight: filter === f.id ? 700 : 500, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, transition: "all 0.15s" }}>
                      {f.label}
                      <span style={{ borderRadius: 10, padding: "1px 6px", fontSize: 11, fontWeight: 800, background: filter === f.id ? "#E7A432" : "#e8eef2", color: filter === f.id ? "#163244" : "#5a6e7f" }}>{f.count}</span>
                    </button>
                  ))}
                </div>
              )}

              {loadingData && (
                <div style={{ textAlign: "center", padding: "60px", color: "#7a93a4" }}>
                  <span style={{ display: "inline-block", width: 26, height: 26, border: "3px solid #dce5ec", borderTopColor: "#163244", borderRadius: "50%", animation: "spin 0.7s linear infinite", marginBottom: 12 }}/><br/>
                  Cargando tus solicitudes...
                </div>
              )}

              {loadError && !loadingData && (
                <div style={{ textAlign: "center", padding: "40px", background: "#fff", borderRadius: 14, color: "#c0392b", fontSize: 14 }}>
                  ⚠ {loadError}<br/>
                  <button onClick={fetchRequests} style={{ marginTop: 12, padding: "8px 20px", borderRadius: 8, background: "#163244", border: "none", color: "#fff", fontFamily: font, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Reintentar</button>
                </div>
              )}

              {!loadingData && !loadError && requests.length === 0 && <EmptyState navigate={navigate} />}

              {!loadingData && !loadError && sorted.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE).map(req => (
                <RequestCard key={req.id} req={req}
                  sinResultados={Boolean(sinResultados[req.id])}
                  onViewQuotes={id => navigate(`/mis-solicitudes/${id}/presupuestos`)}
                  onRate={id => navigate(`/calificar/${id}`)}
                  onChat={id => navigate(`/chat/${id}`)}
                  onDispute={req => setDisputeModal(req)}
                  onCancel={handleCancel}
                  onSearchAgain={handleSearchAgain}
                />
              ))}

              {!loadingData && !loadError && requests.length > 0 && sorted.length === 0 && (
                <div style={{ textAlign: "center", padding: "40px", background: "#fff", borderRadius: 14, color: "#7a93a4", fontSize: 13, border: "2px dashed rgba(0,0,0,0.08)" }}>
                  No hay solicitudes en esta categoría.
                </div>
              )}

              {/* Paginación */}
              {!loadingData && !loadError && sorted.length > PAGE_SIZE && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 8 }}>
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid rgba(0,0,0,0.1)", background: "#fff", cursor: page === 1 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#163244", opacity: page === 1 ? 0.35 : 1, fontFamily: font }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
                  </button>
                  {Array.from({ length: Math.ceil(sorted.length / PAGE_SIZE) }, (_, i) => i + 1).map(p => (
                    <button key={p} onClick={() => setPage(p)} style={{ width: 32, height: 32, borderRadius: 8, border: "none", background: page === p ? "#163244" : "#fff", color: page === p ? "#fff" : "#5a6e7f", fontFamily: font, fontWeight: 700, fontSize: 13, cursor: "pointer", border: page === p ? "none" : "1px solid rgba(0,0,0,0.1)" }}>
                      {p}
                    </button>
                  ))}
                  <button
                    onClick={() => setPage(p => Math.min(Math.ceil(sorted.length / PAGE_SIZE), p + 1))}
                    disabled={page === Math.ceil(sorted.length / PAGE_SIZE)}
                    style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid rgba(0,0,0,0.1)", background: "#fff", cursor: page === Math.ceil(sorted.length / PAGE_SIZE) ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#163244", opacity: page === Math.ceil(sorted.length / PAGE_SIZE) ? 0.35 : 1, fontFamily: font }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
                  </button>
                  <span style={{ fontSize: 12, color: "#7a93a4", marginLeft: 4 }}>
                    {(page-1)*PAGE_SIZE + 1}–{Math.min(page*PAGE_SIZE, sorted.length)} de {sorted.length}
                  </span>
                </div>
              )}
            </div>

            {/* Sidebar */}
            {!loadingData && !loadError && <Sidebar requests={requests} navigate={navigate} />}
          </div>
        </div>
      </div>

      {disputeModal && (
        <DisputeModal
          req={disputeModal}
          onClose={() => setDisputeModal(null)}
          onSuccess={() => {
            setDisputeModal(null);
            showToast("Reclamo enviado. El equipo de Fixo lo revisará en breve.");
            fetchRequests();
          }}
        />
      )}

      {toast && (
        <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "#163244", color: "#fff", padding: "13px 26px", borderRadius: 10, fontWeight: 600, fontSize: 14, zIndex: 999, boxShadow: "0 4px 20px rgba(0,0,0,0.2)", borderLeft: "4px solid #E7A432", whiteSpace: "nowrap", fontFamily: font }}>
          {toast}
        </div>
      )}

      <style>{`
        * { box-sizing: border-box; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}
